import { useCallback, useEffect, useState } from 'react';
import { ContentEditorPersonalizationContextProvider } from 'personalization-lib/contexts/ContentEditorPersonalizationContext';
import { UsageTrackerProvider } from 'personalization-lib/contexts/UsageTrackerContext';
import CreateVariantPanel from 'personalization-lib/components/variantCreation/CreateVariantPanel';
import AIVariantCreationInProgressModal from 'personalization-lib/components/variantCreation/AIVariantCreationInProgressModal';
import PersonalizationAblyProvider from 'personalization-lib/components/wrappers/PersonalizationAblyProvider';
import VariantGenerationChannelSubscriber from 'personalization-lib/components/ably/VariantGenerationChannelSubscriber';
import useAssetDataWithDefaultOverride from 'personalization-lib/hooks/contentEditor/useAssetDataWithDefaultOverride';
import usePersonalizationStrategy from 'personalization-lib/hooks/variants/usePersonalizationStrategy';
import useContentEditorPostCreation from 'personalization-lib/hooks/contentEditor/useContentEditorPostCreation';
import useAiExecutionStatus from 'personalization-lib/hooks/variants/useAiExecutionStatus';
import { showVariantGenerationErrorAlert } from 'personalization-lib/utils/alerts';
import { createVariantInitialData } from 'personalization-lib/utils/variantCreation';
import { getPersonalizationAccess } from 'personalization-lib/utils/auth/featureAccess';
import { getUserId } from 'personalization-lib/utils/auth/userPermissions';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
/**
 * A wrapper that enables use of content editor personalization features.
 * @param tracker - The tracker instance.
 * @param appName - The name of the app, used for tracking.
 * @param screen - The name of the screen, used for tracking.
 * @param assetData - The data for the asset being edited.
 * @param children - The children to render.
 * @returns The children component wrapped with the content editor personalization context.
 */
const ContentEditorProvider = ({
  tracker,
  appName,
  screen,
  assetData: initialAssetData,
  children
}) => {
  var _initialAssetData$str;
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [isAiModalOpen, setIsAiModalOpen] = useState(false);
  const [tempStrategyId, setTempStrategyId] = useState(); // For new strategies, we won't get this from the consumer right away, so we need to temporarily set it to get AI variant updates working
  const hasInContextCreation = getPersonalizationAccess().view;
  const strategyId = (_initialAssetData$str = initialAssetData.strategyId) !== null && _initialAssetData$str !== void 0 ? _initialAssetData$str : tempStrategyId;
  const userId = getUserId();
  const {
    assetData,
    loading,
    error
  } = useAssetDataWithDefaultOverride(Object.assign({}, initialAssetData, {
    strategyId
  }));
  const {
    onManualCreateSuccess,
    onAiCreateSuccess
  } = useContentEditorPostCreation(Object.assign({}, initialAssetData, {
    strategyId
  }));
  const {
    refetch: refetchStrategy
  } = usePersonalizationStrategy(strategyId);
  const {
    requestsInProgress
  } = useAiExecutionStatus(strategyId, userId);
  useEffect(() => {
    if (requestsInProgress === 0) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setIsAiModalOpen(false);
    }
  }, [requestsInProgress]);
  const openPanel = useCallback(() => {
    if (!assetData) {
      throw new Error('Cannot open panel without valid asset data');
    }
    setIsPanelOpen(true);
  }, [assetData]);
  const handleCreateSuccess = useCallback((newStrategyId, newAssetData) => {
    if (newAssetData) {
      onManualCreateSuccess(newAssetData);
    } else {
      setIsAiModalOpen(true);
    }
    void refetchStrategy();
    setTempStrategyId(newStrategyId);
  }, [onManualCreateSuccess, refetchStrategy]);
  return /*#__PURE__*/_jsx(UsageTrackerProvider, {
    tracker: tracker,
    appName: appName,
    screen: screen,
    children: /*#__PURE__*/_jsxs(ContentEditorPersonalizationContextProvider, {
      openPanel: openPanel,
      isPanelOpen: isPanelOpen,
      assetData: assetData,
      loading: loading,
      error: error,
      children: [strategyId && hasInContextCreation && /*#__PURE__*/_jsx(PersonalizationAblyProvider, {
        children: /*#__PURE__*/_jsx(VariantGenerationChannelSubscriber, {
          strategyId: strategyId,
          onMessageError: err => console.error(err),
          onGenerationSuccess: info => {
            if (info.userId === userId) {
              onAiCreateSuccess(info, isAiModalOpen);
            }
          },
          onGenerationError: info => {
            if (info.userId === userId) {
              showVariantGenerationErrorAlert(info);
            }
          },
          onMessageProcessed: () => void refetchStrategy()
        })
      }), children, isPanelOpen && /*#__PURE__*/_jsx(CreateVariantPanel, {
        onClose: () => setIsPanelOpen(false),
        initialData: createVariantInitialData(assetData),
        showOverlay: true,
        onCreateSuccess: handleCreateSuccess
      }), isAiModalOpen && hasInContextCreation && /*#__PURE__*/_jsx(AIVariantCreationInProgressModal, {
        onClose: () => setIsAiModalOpen(false)
      })]
    })
  });
};
export default ContentEditorProvider;